import { useNavigate } from "react-router-dom";
import { Avatar, Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Notification } from "../../../types";
import { useFormattedTimestamp } from "../../../hooks/useFormattedTimestamp";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import { colors } from "../../../theme/palette";
import { NotificationType } from "../../../enums/notifications.enum";

const styles = {
  container: {
    width: "100%",
    display: "flex",
    columnGap: "12px",
  },
  avatar: {
    width: "48px",
    height: "48px",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  unreadIndicator: {
    position: "absolute",
    top: "0",
    right: "0",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: "#12B76A",
  },
  header: {
    display: "flex",
    width: "fit-content",
    columnGap: "8px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  textPrimary: {
    color: colors.Dark1000,
  },
};

const NotificationItem = ({
  notification,
  lastSeenTime,
}: {
  notification: Notification;
  lastSeenTime: number | null;
}) => {
  const navigate = useNavigate();

  const { createdAt, type, additionalProperties, count } = notification;
  const { user, post } = additionalProperties;
  const { fullName, firstTwoChr, media, profileLink } = useDataFormatter(user);

  const formattedTime = useFormattedTimestamp(createdAt);
  const IS_UNREAD = !lastSeenTime || createdAt > lastSeenTime;

  const notificationDescriptions: Partial<Record<NotificationType, JSX.Element>> = {
    [NotificationType.EVENT_JOIN_REQUEST]: <EventJoinRequestDescription count={count} eventName={post?.event.name} />,
    [NotificationType.FOLLOW]: <FollowDescription count={count} />,
    [NotificationType.ENTITY_PROFILE_VIEWED]: <EntityProfileViewed count={count} />,
  };

  return (
    <Box sx={styles.container}>
      <Avatar src={media} sx={styles.avatar}>
        {firstTwoChr}
      </Avatar>

      <Box sx={styles.contentContainer}>
        {IS_UNREAD && <Box sx={styles.unreadIndicator} />}

        <Box sx={styles.header}>
          <Link
            variant="body12SemiBold"
            sx={{ ...styles.textPrimary, textDecoration: "none", cursor: "pointer" }}
            onClick={() => navigate(profileLink)}
          >
            {fullName}
          </Link>
          <Typography variant="body10Regular" sx={styles.textPrimary}>
            {formattedTime}
          </Typography>
        </Box>

        {notificationDescriptions[type] ?? null}
      </Box>
    </Box>
  );
};

export default NotificationItem;

const EventJoinRequestDescription = ({ count, eventName }: { count: number; eventName?: string }) => {
  const { t } = useTranslation();
  return (
    <Typography variant="body14Regular" sx={{ color: colors.Dark1000 }}>
      {t("layout.notifications.isRequestingToJoinYourEvent")}
      {eventName && (
        <Typography variant="body14SemiBold" sx={{ color: colors.Green100 }}>{` ${eventName} `}</Typography>
      )}
      {count > 1 && t("layout.notifications.others")}
    </Typography>
  );
};

const FollowDescription = ({ count }: { count: number }) => {
  const { t } = useTranslation();
  return (
    <Typography variant="body14Regular" sx={{ color: colors.Dark1000 }}>
      {t("layout.notifications.startedFollowingYou")}
      {count > 1 && ` ${t("layout.notifications.others")}`}
    </Typography>
  );
};

const EntityProfileViewed = ({ count }: { count: number }) => {
  const { t } = useTranslation();
  return (
    <Typography variant="body14Regular" sx={{ color: colors.Dark1000 }}>
      {t("layout.notifications.visitedYourProfile")}
      {count > 1 && ` ${t("layout.notifications.others")}`}
    </Typography>
  );
};
